import { localizeDateNames } from "@/lib/dateNames";
/**
 * Centralized date/time formatting — Prompt 32.
 *
 * MiseOne MVP is English-first. Never rely on the browser locale
 * for user-facing labels. All date rendering across the app goes
 * through these helpers.
 *
 * Preferred locale: en-US (matches marketing collateral).
 * Do not switch to en-GB without a product-level decision.
 */

export const LOCALE = "en-US";

/** Normalises whatever the caller passes into a Date, or `null` if invalid. */
const toDate = (input) => {
    if (input == null || input === "") return null;
    const d = input instanceof Date ? input : new Date(input);
    return Number.isNaN(d.getTime()) ? null : d;
};

/**
 * Long form — "Monday, July 3, 2026".
 * Use for hero headings on modules (Weekly Menu, Inventory count screen,
 * Daily Prep List, print layouts).
 */
export function formatDateLong(input) {
    const d = toDate(input);
    if (!d) return "";
    return localizeDateNames(d.toLocaleDateString(LOCALE, {
        weekday: "long",
        month: "long",
        day: "numeric",
        year: "numeric",
    }), { longMonth: true });
}

/**
 * Short form — "Jul 3, 2026".
 * Use in dense lists, tables, cards.
 */
export function formatDateShort(input) {
    const d = toDate(input);
    if (!d) return "";
    return localizeDateNames(d.toLocaleDateString(LOCALE, {
        month: "short",
        day: "numeric",
        year: "numeric",
    }));
}

/**
 * Compact — "Jul 3".
 * Use where the year is redundant (weekly menu day tabs, this-week charts).
 */
export function formatDateCompact(input) {
    const d = toDate(input);
    if (!d) return "";
    return localizeDateNames(d.toLocaleDateString(LOCALE, {
        month: "short",
        day: "numeric",
    }));
}

/**
 * Date + time — "Jul 3, 2026, 10:15 AM".
 * Use in audit trails, activity feeds, log entries.
 */
export function formatDateTime(input) {
    const d = toDate(input);
    if (!d) return "";
    return localizeDateNames(d.toLocaleString(LOCALE, {
        month: "short",
        day: "numeric",
        year: "numeric",
        hour: "numeric",
        minute: "2-digit",
    }));
}

/**
 * Header form — "Monday · Jul 3".
 * Compact daily header used in Weekly Menu / Prep List cards.
 */
export function formatDateHeader(input) {
    const d = toDate(input);
    if (!d) return "";
    const weekday = d.toLocaleDateString(LOCALE, { weekday: "long" });
    const md = d.toLocaleDateString(LOCALE, { month: "short", day: "numeric" });
    return localizeDateNames(`${weekday} · ${md}`);
}

/**
 * Standalone weekday — "Monday".
 * Use in table headers, day pickers.
 */
export function formatWeekday(input) {
    const d = toDate(input);
    if (!d) return "";
    return localizeDateNames(d.toLocaleDateString(LOCALE, { weekday: "long" }));
}

/**
 * Standalone month — "July".
 * Use in month pickers, report labels.
 */
export function formatMonth(input) {
    const d = toDate(input);
    if (!d) return "";
    return localizeDateNames(d.toLocaleDateString(LOCALE, { month: "long" }), { longMonth: true });
}
